import { normalizePhoneDigits } from "@/lib/phoneAuth";
import { geniFetch, setAuthTokens } from "./geniBackend";

function phoneDigitsForApi(displayPhone: string): string {
  return normalizePhoneDigits(displayPhone);
}

export type PhoneAuthMode = "register" | "login";

export type FacebookCapiClientPayload = {
  fbLeadEventId?: string;
  fbCompleteRegistrationEventId?: string;
  fbEventSourceUrl?: string;
  fbp?: string;
  fbc?: string;
};

export async function loginWithEmail(email: string, password: string): Promise<void> {
  const response = await geniFetch<{ accessToken: string; refreshToken: string }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  setAuthTokens(response.accessToken, response.refreshToken);
}

export async function registerWithEmail(
  email: string,
  password: string,
  fb?: FacebookCapiClientPayload,
): Promise<void> {
  const response = await geniFetch<{ accessToken: string; refreshToken: string }>("/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, ...fb }),
  });
  setAuthTokens(response.accessToken, response.refreshToken);
}

export async function startPhoneAuth(
  mode: PhoneAuthMode,
  phone: string,
  password: string,
  captchaToken?: string,
  fb?: FacebookCapiClientPayload,
): Promise<{ challengeId: string; resendAvailableInSeconds: number }> {
  return geniFetch<{ challengeId: string; resendAvailableInSeconds: number }>("/auth/phone/start", {
    method: "POST",
    body: JSON.stringify({
      mode,
      phone: phoneDigitsForApi(phone),
      password,
      ...(captchaToken ? { captchaToken } : {}),
      ...fb,
    }),
  });
}

export async function loginWithPhone(phone: string, password: string): Promise<void> {
  const response = await geniFetch<{ accessToken: string; refreshToken: string }>("/auth/phone/login", {
    method: "POST",
    body: JSON.stringify({ phone: phoneDigitsForApi(phone), password }),
  });
  setAuthTokens(response.accessToken, response.refreshToken);
}

export async function verifyPhoneOtp(
  challengeId: string,
  otp: string,
  fb?: FacebookCapiClientPayload,
): Promise<void> {
  const response = await geniFetch<{ accessToken: string; refreshToken: string }>("/auth/phone/verify", {
    method: "POST",
    body: JSON.stringify({ challengeId, otp, ...fb }),
  });
  setAuthTokens(response.accessToken, response.refreshToken);
}

export async function resendPhoneOtp(challengeId: string): Promise<{ challengeId: string; resendAvailableInSeconds: number }> {
  return geniFetch<{ challengeId: string; resendAvailableInSeconds: number }>("/auth/phone/resend", {
    method: "POST",
    body: JSON.stringify({ challengeId }),
  });
}

export async function startPasswordReset(
  phone: string,
): Promise<{ challengeId: string; resendAvailableInSeconds: number }> {
  return geniFetch<{ challengeId: string; resendAvailableInSeconds: number }>("/auth/password/reset/start", {
    method: "POST",
    body: JSON.stringify({ phone: phoneDigitsForApi(phone) }),
  });
}

export async function resendPasswordResetOtp(
  challengeId: string,
): Promise<{ challengeId: string; resendAvailableInSeconds: number }> {
  return geniFetch<{ challengeId: string; resendAvailableInSeconds: number }>("/auth/password/reset/resend", {
    method: "POST",
    body: JSON.stringify({ challengeId }),
  });
}

export async function verifyPasswordResetOtp(
  challengeId: string,
  otp: string,
): Promise<void> {
  await geniFetch<{ ok: true }>("/auth/password/reset/verify", {
    method: "POST",
    body: JSON.stringify({ challengeId, otp }),
  });
}

export async function confirmPasswordReset(
  challengeId: string,
  otp: string,
  newPassword: string,
): Promise<void> {
  const response = await geniFetch<{ accessToken: string; refreshToken: string }>("/auth/password/reset/confirm", {
    method: "POST",
    body: JSON.stringify({ challengeId, otp, newPassword }),
  });
  setAuthTokens(response.accessToken, response.refreshToken);
}

export async function loginWithGoogleIdToken(
  idToken: string,
  fb?: FacebookCapiClientPayload,
): Promise<{ isNewUser: boolean }> {
  const response = await geniFetch<{ accessToken: string; refreshToken: string; isNewUser: boolean }>("/auth/social/google", {
    method: "POST",
    body: JSON.stringify({ idToken, ...fb }),
  });
  setAuthTokens(response.accessToken, response.refreshToken);
  return { isNewUser: response.isNewUser };
}

export async function loginWithAppleIdToken(
  idToken: string,
  fb?: FacebookCapiClientPayload,
): Promise<{ isNewUser: boolean }> {
  const response = await geniFetch<{ accessToken: string; refreshToken: string; isNewUser: boolean }>("/auth/social/apple", {
    method: "POST",
    body: JSON.stringify({ idToken, ...fb }),
  });
  setAuthTokens(response.accessToken, response.refreshToken);
  return { isNewUser: response.isNewUser };
}

export async function linkGoogleIdToken(idToken: string): Promise<void> {
  await geniFetch<{ ok: true }>("/account/identities/google", {
    method: "POST",
    body: JSON.stringify({ idToken }),
  });
}

export async function unlinkGoogleAccount(): Promise<void> {
  await geniFetch<{ ok: true }>("/account/identities/google", {
    method: "DELETE",
  });
}
